import { useState, useEffect } from "react";
import api from "../api/axios";
import toast from "react-hot-toast";
import getInitials from "../utils/getInitials";

function resolveUploadUrl(path) {
  if (/^https?:\/\//i.test(path)) return path;
  const baseUrl = api.defaults.baseURL || "/api";
  const backendOrigin = baseUrl.replace(/\/api\/?$/, "");
  return `${backendOrigin}${path.startsWith("/") ? path : `/${path}`}`;
}

function KycDocumentPreview({ label, path }) {
  if (!path) {
    return (
      <div className="rounded-xl border border-white/10 bg-slate-950/50 p-3">
        <p className="text-xs font-medium text-slate-300">{label}</p>
        <p className="mt-2 text-sm text-slate-500">No file uploaded</p>
      </div>
    );
  }

  const fileUrl = resolveUploadUrl(path);
  const isImage = /\.(jpe?g|png|webp|gif|bmp)(\?.*)?$/i.test(path);

  return (
    <div className="overflow-hidden rounded-xl border border-white/10 bg-slate-950/50">
      <p className="px-3 pt-3 text-xs font-medium text-slate-300">{label}</p>
      {isImage ? (
        <a href={fileUrl} target="_blank" rel="noreferrer" aria-label={`Open ${label}`}>
          <img
            src={fileUrl}
            alt={label}
            loading="lazy"
            className="mt-2 h-40 w-full bg-black/20 object-contain"
          />
        </a>
      ) : (
        <a
          href={fileUrl}
          target="_blank"
          rel="noreferrer"
          className="m-3 inline-flex rounded-lg border border-cyan-300/20 bg-cyan-300/10 px-3 py-2 text-sm font-medium text-cyan-200 hover:bg-cyan-300/15"
        >
          Open document
        </a>
      )}
    </div>
  );
}

export default function AdminKYC() {
  const [kycs, setKycs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState("");

  // Fetch pending KYCs
  useEffect(() => {
    const fetchKycs = async () => {
      try {
        const res = await api.get("/admin/kyc/pending");
        const data = res.data;
        if (res.status === 200) setKycs(data);
      } catch (err) {
        console.error(err);
        toast.error("Failed to fetch pending KYC users");
      } finally {
        setLoading(false);
      }
    };
    fetchKycs();
  }, []);

  const handleDecision = async (userId, decision) => {
    if (processingId) return;
    setProcessingId(userId);
    try {
      const mappedStatus = decision === "Approved" ? "verified" : "rejected";
      const res = await api.put(`/admin/kyc/${userId}`, { status: mappedStatus });
      const data = res.data;
      if (res.status === 200) {
        setKycs((prev) => prev.filter((k) => k._id !== userId));
        toast.success(data.message || "KYC updated");
      } else {
        toast.error(data.message || "Failed to update KYC");
      }
    } catch (err) {
      console.error(err);
      toast.error("Server error. Try again later.");
    } finally {
      setProcessingId("");
    }
  };

  const pendingCount = kycs.length;

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-4 md:p-6">
      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 text-white shadow-2xl shadow-cyan-500/10 backdrop-blur">
        <p className="text-xs uppercase tracking-[0.3em] text-cyan-200/80">Compliance Desk</p>
        <h1 className="mt-2 text-2xl font-semibold md:text-3xl">Pending KYC Submissions</h1>
        <p className="mt-2 text-sm text-slate-300">Review user identity submissions and finalize verification decisions.</p>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-5 text-white shadow-xl shadow-black/20 backdrop-blur">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Pending Users</p>
          <p className="mt-2 text-2xl font-semibold">{pendingCount}</p>
        </div>
        <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-5 text-white shadow-xl shadow-black/20 backdrop-blur">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Verification Queue</p>
          <p className="mt-2 text-2xl font-semibold">Manual Review</p>
        </div>
        <div className="rounded-3xl border border-cyan-300/20 bg-cyan-500/10 p-5 text-cyan-100 shadow-xl shadow-cyan-900/30 backdrop-blur">
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-200/80">Status</p>
          <p className="mt-2 text-2xl font-semibold">{loading ? "Loading" : "Ready"}</p>
        </div>
      </section>

      <section className="rounded-[2rem] border border-white/10 bg-slate-900/70 p-5 text-white shadow-2xl shadow-black/30 backdrop-blur md:p-6">
        {loading && <div className="rounded-2xl border border-white/10 bg-slate-800/60 p-4 text-slate-300">Loading KYCs...</div>}
        {!loading && !kycs.length && (
          <div className="rounded-2xl border border-white/10 bg-slate-800/60 p-4 text-slate-300">No pending KYCs.</div>
        )}
        <div className="space-y-3">
          {kycs.map((k) => (
            <div
              key={k._id}
              className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-slate-800/60 p-4 shadow-lg shadow-black/20"
            >
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-4">
                  <div
                    aria-label={`${k.fullName || "User"} initials`}
                    className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full border border-cyan-300/25 bg-cyan-300/10 text-lg font-semibold text-cyan-200"
                  >
                    {getInitials(k.fullName)}
                  </div>
                  <div>
                    <p className="font-semibold text-white">{k.fullName}</p>
                    <p className="text-sm text-slate-300">{k.email}</p>
                    <p className="text-sm text-slate-300">
                      Status: <span className="font-medium text-cyan-200">{k.kycStatus}</span>
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDecision(k._id, "Approved")}
                    disabled={Boolean(processingId)}
                    aria-busy={processingId === k._id}
                    className="rounded-xl border border-emerald-400/40 bg-emerald-500/20 px-4 py-2 text-sm font-medium text-emerald-100 transition hover:bg-emerald-500/30"
                  >
                    {processingId === k._id ? "Saving…" : "Approve"}
                  </button>
                  <button
                    onClick={() => handleDecision(k._id, "Rejected")}
                    disabled={Boolean(processingId)}
                    aria-busy={processingId === k._id}
                    className="rounded-xl border border-rose-400/40 bg-rose-500/20 px-4 py-2 text-sm font-medium text-rose-100 transition hover:bg-rose-500/30"
                  >
                    {processingId === k._id ? "Saving…" : "Reject"}
                  </button>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <KycDocumentPreview label="Identity document" path={k.kycInfo?.idFile} />
                <KycDocumentPreview label="Selfie" path={k.kycInfo?.selfie} />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
